import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { createEnumDelimitedArrayParam, createEnumParam, StringParam, withDefault } from 'use-query-params'

import useFiltering from '@shared/hooks/src/useFiltering'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import API from '@shared/services/src/API'
import { flatten, pageParam, QK } from '@shared/utils'

import { FilterType } from '@components/TableFilters'

const LIMIT = 25

export function useSOPs() {
  const [{ sopsSearch, sopsPolicy, sopsCategories }] = useSOPsFilters()

  const query = {
    limit: LIMIT,
    search_term: sopsSearch,
    policy: sopsPolicy,
    categories: sopsCategories,
  }

  return useInfiniteQuery({
    queryKey: QK.sops.list(query),
    queryFn: ({ pageParam }) => API.sops.list({ ...query, offset: pageParam * LIMIT }),
    select: flatten,
    initialPageParam: 0,
    getNextPageParam: pageParam(LIMIT),
  })
}

const ViewParam = createEnumParam(['list', 'view', 'create'])

export function useSOPsFilters() {
  const policies = useLookup(Lookup.SOPPolicies)
  const categories = useLookup(Lookup.SOPCategories)

  return useFiltering({
    sops: ViewParam,
    sopId: StringParam,
    sopsSearch: StringParam,
    sopsPolicy: createEnumParam(policies),
    sopsCategories: withDefault(createEnumDelimitedArrayParam(categories), []),
  })
}

export const filtersResetState = {
  sopsSearch: undefined,
  sopsPolicy: undefined,
  sopsCategories: undefined,
}

export function areFiltersApplied(filters) {
  if (filters.sopsSearch) return true
  if (filters.sopsPolicy) return true
  if (filters.sopsCategories?.length > 0) return true
  return false
}

export function useFiltersSchema() {
  const policies = useLookup(Lookup.SOPPolicies)
  const categories = useLookup(Lookup.SOPCategories)

  return useMemo(() => {
    return [
      {
        key: 'sopsPolicy',
        label: 'Policy',
        type: FilterType.SELECT,
        options: [{ label: 'All', value: undefined, hidden: true }, ...policies.map((value) => ({ label: value, value }))],
      },
      {
        key: 'sopsCategories',
        label: 'Category',
        type: FilterType.MULTI_SELECT,
        options: categories.map((value) => ({ label: value, value })),
      },
    ]
  }, [categories, policies])
}
